import { Link } from 'react-router-dom';
import { Aviso, Botao, Selo, formatarMoeda, formatarPercentual } from '../../../componentes/ui';
import type {
  CadastroNaProspeccao,
  ContatoSiscred,
  PessoaDoCadastro,
} from '../../../servicos/prospeccao';
import { rotuloDoTipo } from '../../cliente/ConfiguracaoDeCredito';
import { ErroDaApi } from '../../ErroDaApi';

/**
 * A empresa no cadastro do Deca, dentro do painel da Prospecção: colaboradores,
 * contatos do CRM, telefone e e-mail, e o deságio de cada tipo de crédito, se
 * é fornecedora.
 *
 * É consulta. O cadastro e as anotações da Prospecção continuam separados —
 * nada daqui é gravado nas anotações sozinho. "Anotar" traz a pessoa para os
 * contatos do painel, e ela fica nas anotações quando se salva, como qualquer
 * contato digitado.
 */
export function NoCadastroDoDeca(props: {
  readonly cadastro: CadastroNaProspeccao | null | undefined;
  readonly erro: unknown;
  /** Os contatos anotados agora no painel — quem já está neles não se anota de novo. */
  readonly anotados: readonly ContatoSiscred[];
  /** O painel aceita até 20 contatos. */
  readonly semEspaco: boolean;
  readonly aoAnotar: (pessoa: PessoaDoCadastro) => void;
}): JSX.Element | null {
  const c = props.cadastro;
  if (props.erro !== null) {
    return <ErroDaApi titulo="Não foi possível ler o cadastro da empresa" erro={props.erro} />;
  }
  if (c === undefined) {
    return <p className="text-sm text-conteudo-sutil">Lendo o cadastro da empresa…</p>;
  }
  if (c === null) return null;

  const pessoas: { readonly pessoa: PessoaDoCadastro; readonly origem: string }[] = [
    ...c.colaboradores.map((pessoa) => ({ pessoa, origem: 'Colaborador' })),
    ...c.contatos.map((pessoa) => ({ pessoa, origem: 'Contato do CRM' })),
  ];
  const perfis = [
    c.perfis.cliente && 'Cliente',
    c.perfis.assessor && 'Assessor',
    c.perfis.fornecedor && 'Fornecedor',
  ].filter((p): p is string => typeof p === 'string');

  return (
    <section className="flex flex-col gap-3 rounded-card border border-borda bg-superficie-base/40 p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <span className="flex flex-wrap items-center gap-2">
          <h3 className="text-sm font-semibold text-conteudo-primario">No cadastro do Deca</h3>
          {perfis.map((p) => (
            <Selo key={p} tom="informativo" tamanho="pequeno">
              {p}
            </Selo>
          ))}
        </span>
        <Link
          to={`/empresas/${c.businessId}`}
          className="text-sm font-medium underline underline-offset-2"
        >
          Abrir cadastro
        </Link>
      </div>

      {!c.ativa && (
        <Aviso tom="atencao" titulo="Desativada no cadastro">
          Reative-a em Empresas para levá-la ao CRM.
        </Aviso>
      )}

      <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
        <dt className="text-conteudo-sutil">Telefone</dt>
        <dd className="tabular-nums">{c.telefone ?? '—'}</dd>
        <dt className="text-conteudo-sutil">E-mail</dt>
        <dd className="break-all">{c.email}</dd>
      </dl>

      {pessoas.length === 0 ? (
        <p className="text-sm text-conteudo-sutil">
          Nenhum colaborador nem contato no cadastro da empresa.
        </p>
      ) : (
        <ul className="flex flex-col divide-y divide-borda/60">
          {pessoas.map(({ pessoa, origem }, i) => {
            const jaEsta = jaAnotada(pessoa, props.anotados);
            return (
              <li key={i} className="flex flex-wrap items-center justify-between gap-3 py-2">
                <span className="flex min-w-0 flex-col gap-0.5 text-sm">
                  <span className="font-medium text-conteudo-primario">
                    {[pessoa.nome, pessoa.cargo].filter(Boolean).join(' — ') || 'Sem nome'}
                  </span>
                  <span className="text-xs text-conteudo-secundario">
                    {[origem, pessoa.telefone, pessoa.email].filter(Boolean).join(' · ')}
                  </span>
                </span>
                {jaEsta ? (
                  <span className="text-xs text-conteudo-sutil">Já está nos contatos</span>
                ) : (
                  <Botao
                    variante="sutil"
                    tamanho="pequeno"
                    disabled={props.semEspaco}
                    onClick={() => props.aoAnotar(pessoa)}
                  >
                    Anotar
                  </Botao>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {c.creditosDoFornecedor.length > 0 && (
        <div className="flex flex-col gap-1">
          <h4 className="text-xs font-medium text-conteudo-sutil">Deságio do fornecedor</h4>
          <ul className="flex flex-col gap-0.5 text-sm">
            {c.creditosDoFornecedor.map((cr) => (
              <li key={cr.tipoCredito} className="flex flex-wrap justify-between gap-x-3">
                <span>
                  {rotuloDoTipo(cr.tipoCredito)}:{' '}
                  <strong className="tabular-nums">{formatarPercentual(cr.desagio)}</strong>
                </span>
                {cr.credito > 0 && (
                  <span className="text-xs tabular-nums text-conteudo-sutil">
                    {formatarMoeda(cr.credito)} habilitados
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

/** A mesma pessoa já anotada: pelo e-mail, pelo telefone ou pelo nome. */
export function jaAnotada(pessoa: PessoaDoCadastro, anotados: readonly ContatoSiscred[]): boolean {
  const email = (v: string | null) => (v ?? '').trim().toLowerCase();
  const digitos = (v: string | null) => (v ?? '').replace(/\D/g, '').replace(/^0+/, '');
  const nome = (v: string | null) => (v ?? '').trim().toLowerCase();
  return anotados.some(
    (a) =>
      (email(pessoa.email) !== '' && email(a.email) === email(pessoa.email)) ||
      (digitos(pessoa.telefone) !== '' && digitos(a.telefone) === digitos(pessoa.telefone)) ||
      (nome(pessoa.nome) !== '' && nome(a.responsavel) === nome(pessoa.nome)),
  );
}
